<template>
  <div class="category-tree-item" :data-category-id="category.id">
    <div
      class="category-header"
      :class="{
        'selected-category': selectedCategory === category.id,
        'inactive-category': !category.isActive,
        'no-products-category': !category.has_products
      }"
      @click="selectCategory(category.id)"
      :data-category-id="category.id"
    >
      <div class="flex items-center gap-2 w-full">
        <!-- Expansion icon (only when it has children) -->
        <svg
          v-if="hasChildren"
          class="w-5 h-5 flex-shrink-0 transition-transform duration-300 cursor-pointer"
          :class="{ 'rotate-[-90deg]': !isExpanded, 'rotate-0': isExpanded }"
          viewBox="0 0 24 24"
          :fill="selectedCategory === category.id ? '#FBBD1E' : '#000000'"
          @click.stop="toggleExpand"
        >
          <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/>
        </svg>

        <!-- Spacer for categories without children (to keep the alignment) -->
        <div v-else class="w-5 flex-shrink-0"></div>

        <!-- Category name with truncation and tooltip -->
        <div class="flex-1 overflow-hidden">
          <span
            class="font-archivo text-[15px] leading-[18px] block truncate font-medium"
            :class="selectedCategory === category.id ? 'text-[#FBBD1E] font-semibold' : 'text-gray-800'"
            :title="category.name"
          >
            {{ $filters.formatCategoryName(category.name) }}
          </span>
        </div>

        <!-- Product indicator -->
        <span
          v-if="category.has_products || category.productCount"
          class="text-xs ml-1 flex-shrink-0 font-medium"
          :class="selectedCategory === category.id ? 'text-[#FBBD1E]' : 'text-gray-600'"
        >
          ({{ category.productCount || getProductCount(category) }})
        </span>

        <!-- Clear button (X) when selected -->
        <button
          v-if="selectedCategory === category.id"
          class="ml-2 p-1 rounded hover:bg-gray-200 transition clear-x-btn"
          title="{{$t ? $t('common.clear') : 'Clear'}}"
          @click.stop="$emit('select', category.id)"
          aria-label="Deselect category"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-4 h-4 text-gray-600 hover:text-red-600">
            <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
          </svg>
        </button>
      </div>
    </div>

    <!-- Subcategorias (filhos) -->
    <div
      v-if="hasChildren && isExpanded"
      class="subcategories pl-6 mt-2"
    >
      <div
        v-for="child in category.children"
        :key="child.id"
        class="category-item mb-2"
        :data-category-id="child.id"
      >
        <CategoryTreeItem
          :category="child"
          :selected-category="selectedCategory"
          @select="$emit('select', $event)"
          @ensure-visible="$emit('ensure-visible', $event)"
        />
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CategoryTreeItem',
  props: {
    category: {
      type: Object,
      required: true
    },
    selectedCategory: {
      type: String,
      default: null
    }
  },
  emits: ['select', 'ensure-visible'],
  data() {
    return {
      isExpanded: this.category?.expanded || false
    }
  },

  watch: {
    // Watches changes in the category expanded property
    'category.expanded': {
      immediate: true,
      handler(newValue) {
        if (newValue === true) {
          this.isExpanded = true;
        }
      }
    },
    // Watches changes in the selected category
    selectedCategory: {
      immediate: true,
      handler(newValue) {
        // Checks whether the current category or any of its descendants is selected
        if (newValue) {
          this.expandIfSelected(newValue);

          // If the current category is the selected one, keep it expanded
          if (newValue === this.category.id && this.hasChildren) {
            this.isExpanded = true;
            this.$emit('ensure-visible', this.category.id);
          }
        }
      }
    }
  },
  computed: {
    hasChildren() {
      return this.category?.children && this.category.children.length > 0
    }
  },
  methods: {
    toggleExpand(event) {
      // Changes the local expansion state instead of the prop
      this.isExpanded = !this.isExpanded
      event.stopPropagation()
    },
    selectCategory(categoryId) {
      // Temporarily removing the product check for debugging
      // if (this.category.has_products === false) {
      //   return
      // }

      // If the category has children, keep it expanded when it is selected
      if (this.hasChildren) {
        this.isExpanded = true
      }

      // Emit the selection event with the category ID
      this.$emit('select', categoryId)
    },
    getProductCount(category) {
      // Here you can implement logic to count products
      // For now, we return a fixed value or a random value for demonstration
      return category.productCount || ''
    },
    // Checks whether the current category or any of its descendants is selected
    expandIfSelected(selectedId) {
      // If the current category is selected, there is nothing to do
      if (this.category.id === selectedId) {
        return true;
      }

      // If the category has children, check whether any of them is selected
      if (this.hasChildren) {
        for (const child of this.category.children) {
          // Checks whether the child is selected
          if (child.id === selectedId) {
            this.isExpanded = true;
            return true;
          }

          // Recursively checks whether any descendant is selected
          const hasSelectedDescendant = this.checkDescendants(child, selectedId);
          if (hasSelectedDescendant) {
            this.isExpanded = true;
            return true;
          }
        }
      }

      return false;
    },
    // Recursively checks whether any descendant is selected
    checkDescendants(category, selectedId) {
      // If the category is selected, return true
      if (category.id === selectedId) {
        return true;
      }

      // If the category has children, check whether any of them is selected
      if (category.children && category.children.length > 0) {
        for (const child of category.children) {
          // Checks whether the child is selected
          if (child.id === selectedId) {
            return true;
          }

          // Recursively checks whether any descendant is selected
          const hasSelectedDescendant = this.checkDescendants(child, selectedId);
          if (hasSelectedDescendant) {
            return true;
          }
        }
      }

      return false;
    }
  }
}
</script>

<style scoped>
.category-tree-item {
  margin-bottom: 0.5rem;
}

.category-header {
  padding: 10px 12px;
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.3s ease;
  border-left: 3px solid transparent;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  max-height: 42px;
  overflow: hidden;
  background-color: #f8f8f8;
}

.category-header:hover {
  background-color: rgba(251, 189, 30, 0.1);
  border-left: 3px solid #FBBD1E;
  transform: translateX(2px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.selected-category {
  background-color: rgba(251, 189, 30, 0.2);
  border-left: 3px solid #FBBD1E;
  box-shadow: 0 2px 5px rgba(251, 189, 30, 0.3);
}

/* X icon to unselect */
.clear-x-btn:hover .w-4.h-4 {
  color: #dc2626; /* red-600 */
}

/* Style for inactive categories */
.inactive-category {
  opacity: 0.7;
  background-color: #eaeaea;
  border-left: 3px solid #cccccc;
}

.inactive-category:hover {
  background-color: #e5e5e5;
  border-left: 3px solid #bbbbbb;
}

/* Style for categories without products */
.no-products-category {
  opacity: 0.7;
  cursor: default;
  background-color: #f0f0f0;
  border-left: 3px solid #dddddd;
}

.no-products-category:hover {
  background-color: #e8e8e8;
  border-left: 3px solid #cccccc;
  transform: none;
}

.subcategories {
  margin-left: 0.25rem;
  padding-left: 0.75rem;
  border-left: 2px dashed rgba(251, 189, 30, 0.6);
  margin-top: 0.25rem;
  margin-bottom: 0.25rem;
  max-height: 300px;
  overflow-y: auto;
  padding-right: 5px;
}

/* Scrollbar styling */
.subcategories::-webkit-scrollbar {
  width: 5px;
}

.subcategories::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.subcategories::-webkit-scrollbar-thumb {
  background: #FBBD1E;
  border-radius: 10px;
  border: 1px solid rgba(0, 0, 0, 0.1);
}

.subcategories::-webkit-scrollbar-thumb:hover {
  background: #e0a800;
  box-shadow: 0 0 2px rgba(0, 0, 0, 0.2);
}

/* Animation for the expansion icon */
.w-5.h-5 {
  transition: transform 0.3s ease;
}

/* Style for the category text */
.font-archivo {
  font-weight: 500;
  letter-spacing: 0.01em;
  transition: color 0.3s ease;
}

.category-header:hover .font-archivo {
  color: #FBBD1E !important;
}

/* Style for the product counter */
.text-xs {
  font-size: 0.75rem;
  opacity: 0.8;
}
</style>
